import { z } from 'zod';

import {
  QUIZ_GROUP_MIN_TERM,
  QUIZ_GROUP_MIN_USERS,
  QUIZ_TERM_MAX,
  QUIZ_USER_COUNT_MAX,
  QUOTE_PLAN_TYPES,
  QUOTE_PLANS,
} from '../constants/quizQuote.const';

/** onzep 견적 폼과 동일: 휴대폰, 유선(02/03~06), 070, 대표번호(15xx~19xx) */
const KR_PHONE_RE =
  /^(01[016789]-?\d{3,4}-?\d{4}|02-?\d{3,4}-?\d{4}|0[3-6]\d-?\d{3,4}-?\d{4}|070-?\d{3,4}-?\d{4}|1[5-9]\d{2}-?\d{4})$/;
/** 서버가 요구하는 시작일 포맷 (%Y-%m-%d) */
const START_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 견적 폼 스키마 — 검증·타입·resolver의 단일 소스.
 * 서버(exhibition) 검증 규칙을 미러링한다.
 */
export const quizQuoteSchema = z
  .object({
    school_name: z
      .string()
      .trim()
      .min(1, '학교·기관명을 입력해 주세요.')
      .max(255),
    account_name: z
      .string()
      .trim()
      .min(1, '담당자명을 입력해 주세요.')
      .max(255),
    account_phone: z
      .string()
      .trim()
      .regex(KR_PHONE_RE, '올바른 연락처를 입력해 주세요.')
      .max(20),
    account_email: z
      .string()
      .trim()
      .min(1, '이메일을 입력해 주세요.')
      .min(3, '이메일을 3자 이상 입력해 주세요.')
      .email('올바른 이메일을 입력해 주세요.')
      .max(255),
    plan: z.enum(QUOTE_PLANS),
    plan_type: z.enum(QUOTE_PLAN_TYPES),
    user_count: z
      .number({ invalid_type_error: '이용 인원을 입력해 주세요.' })
      .int()
      .min(1, '이용 인원을 입력해 주세요.')
      .max(QUIZ_USER_COUNT_MAX),
    term: z
      .number({ invalid_type_error: '이용 기간을 입력해 주세요.' })
      .int()
      .min(1, '이용 기간을 입력해 주세요.')
      .max(QUIZ_TERM_MAX),
    start_date: z.string().regex(START_DATE_RE, '시작일을 선택해 주세요.'),
    annotation: z.string().trim().max(500).default(''),
    consent: z
      .boolean()
      .refine(v => v === true, '개인정보 수집·이용에 동의해 주세요.'),
  })
  .refine(
    v => v.plan_type !== 'group' || v.user_count >= QUIZ_GROUP_MIN_USERS,
    {
      path: ['user_count'],
      message: `학교·단체 요금제는 최소 ${QUIZ_GROUP_MIN_USERS}명부터 신청할 수 있습니다.`,
    },
  )
  .refine(v => v.plan_type !== 'group' || v.term >= QUIZ_GROUP_MIN_TERM, {
    path: ['term'],
    message: `학교·단체 요금제는 최소 ${QUIZ_GROUP_MIN_TERM}개월부터 신청할 수 있습니다.`,
  });

export type QuizQuoteFormValues = z.infer<typeof quizQuoteSchema>;
